import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { Injectable, Injector, Type } from '@angular/core';
import { Observable, race } from 'rxjs';
import { finalize, mapTo, take } from 'rxjs/operators';

import { ModalDialogComponent } from '../../components/modal-dialog/modal-dialog.component';

import { Dialog, DIALOG_COMPONENT, MODAL_OPTIONS, ModalOptions } from './modal-types';

@Injectable({ providedIn: 'root' })
export class ModalService {
    constructor(private overlay: Overlay, private injector: Injector) {}

    
    fromComponent<R, T extends Dialog<R>>(
        component: Type<T>,
        options?: ModalOptions<T>,
    ): Observable<R | undefined> {
        const positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
        const scrollStrategy = this.overlay.scrollStrategies.block();
        const overlayRef = this.overlay.create(new OverlayConfig({
            scrollStrategy,
            positionStrategy,
            hasBackdrop: true,
            panelClass: "vsf-modal-pane",
        }));

        const injector = this.createInjector(
            component as Type<object>,
            options as ModalOptions<object> | undefined,
        );
        const portal = new ComponentPortal(
            ModalDialogComponent,
            undefined,
            injector,
        ) as ComponentPortal<ModalDialogComponent<R, T>>;
        const modal = overlayRef.attach(portal);
        modal.changeDetectorRef.markForCheck();
        const close$ = new Observable<R>(subscriber => {
            modal.instance.closeModal = (result?: R) => {
                subscriber.next(result);
                subscriber.complete();
            };
        });
        const backdropClick$ = overlayRef.backdropClick().pipe(mapTo(undefined));

        return race<R | undefined>(close$, backdropClick$).pipe(
            take(1),
            finalize(() => overlayRef.dispose()),
        );
    }

    private createInjector(
        component: Type<object>,
        options?: ModalOptions<object>,
    ): PortalInjector {
        const weakMap = new WeakMap<object, object>();
        weakMap.set(DIALOG_COMPONENT, component);
        if (options) {
            weakMap.set(MODAL_OPTIONS, options as object);
        }
        return new PortalInjector(this.injector, weakMap);
    }
}
